import { useCallback, useEffect, useState } from "react";
import {
  WELLNESS_DIMENSIONS,
  type WellnessDimension,
  type WellnessFocus,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  archiveWellnessFocus,
  createWellnessFocus,
  listWellness,
  publishWellnessFocus,
  updateWellnessFocus,
} from "@/lib/wellness-api";

// TES-212 — Wellness Plan authoring. Three fixed dimensions (nutrition /
// movement / mind), each a current focus + next step (sv+en). Edited in place;
// publish/archive flip status. One page with an inline editor per dimension.

type Draft = {
  emoji: string;
  titleSv: string;
  titleEn: string;
  focusSv: string;
  focusEn: string;
  nextSv: string;
  nextEn: string;
  // TES-220/221/222 — progression + CTA (strings in the form; empty = unset).
  weekCurrent: string;
  weekTotal: string;
  ctaLabelSv: string;
  ctaLabelEn: string;
  ctaRoute: string;
  status: WellnessFocus["status"] | "new";
};

const EMPTY: Draft = {
  emoji: "",
  titleSv: "",
  titleEn: "",
  focusSv: "",
  focusEn: "",
  nextSv: "",
  nextEn: "",
  weekCurrent: "",
  weekTotal: "",
  ctaLabelSv: "",
  ctaLabelEn: "",
  ctaRoute: "",
  status: "new",
};

function toDraft(f: WellnessFocus): Draft {
  return {
    emoji: f.emoji ?? "",
    titleSv: f.title.sv,
    titleEn: f.title.en,
    focusSv: f.focus.sv,
    focusEn: f.focus.en,
    nextSv: f.nextStep.sv,
    nextEn: f.nextStep.en,
    weekCurrent: f.weekCurrent?.toString() ?? "",
    weekTotal: f.weekTotal?.toString() ?? "",
    ctaLabelSv: f.ctaLabel?.sv ?? "",
    ctaLabelEn: f.ctaLabel?.en ?? "",
    ctaRoute: f.ctaRoute ?? "",
    status: f.status,
  };
}

const DIMENSION_LABELS: Record<WellnessDimension, string> = {
  nutrition: "🥗 Kost",
  movement: "💪 Rörelse",
  mind: "🤍 Sinne",
};

export function WellnessRoute() {
  const { signOut } = useAuth();
  const [drafts, setDrafts] = useState<Record<string, Draft> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleErr = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const load = useCallback(() => {
    setError(null);
    listWellness()
      .then((rows) => {
        const byDim = new Map(rows.map((r) => [r.dimension, r]));
        const next: Record<string, Draft> = {};
        for (const dim of WELLNESS_DIMENSIONS) {
          const row = byDim.get(dim);
          next[dim] = row ? toDraft(row) : { ...EMPTY };
        }
        setDrafts(next);
      })
      .catch(handleErr);
  }, [handleErr]);

  useEffect(load, [load]);

  function patch(dim: string, p: Partial<Draft>) {
    setDrafts((d) => (d ? { ...d, [dim]: { ...d[dim]!, ...p } } : d));
  }

  function payload(dim: WellnessDimension, draft: Draft) {
    const status = draft.status === "new" ? "draft" : draft.status;
    const weekCurrent = draft.weekCurrent.trim()
      ? Number(draft.weekCurrent)
      : null;
    const weekTotal = draft.weekTotal.trim() ? Number(draft.weekTotal) : null;
    // CTA is all-or-nothing: only send it when label (both locales) + route
    // are present, matching the contract's paired refine.
    const hasCta =
      draft.ctaLabelSv.trim() &&
      draft.ctaLabelEn.trim() &&
      draft.ctaRoute.trim();
    return {
      dimension: dim,
      emoji: draft.emoji || null,
      title: { sv: draft.titleSv, en: draft.titleEn },
      focus: { sv: draft.focusSv, en: draft.focusEn },
      nextStep: { sv: draft.nextSv, en: draft.nextEn },
      weekCurrent,
      weekTotal,
      ctaLabel: hasCta ? { sv: draft.ctaLabelSv, en: draft.ctaLabelEn } : null,
      ctaRoute: hasCta ? draft.ctaRoute.trim() : null,
      status,
    };
  }

  // A partially-filled CTA (some but not all of sv label / en label / route)
  // would otherwise be silently dropped by payload()'s all-or-nothing gate.
  // Surface it so the author isn't surprised by lost input.
  function ctaPartial(draft: Draft): boolean {
    const parts = [
      draft.ctaLabelSv.trim(),
      draft.ctaLabelEn.trim(),
      draft.ctaRoute.trim(),
    ];
    const filled = parts.filter(Boolean).length;
    return filled > 0 && filled < parts.length;
  }

  async function run(dim: WellnessDimension, op: () => Promise<WellnessFocus>) {
    setBusy(dim);
    setError(null);
    setNotice(null);
    try {
      const saved = await op();
      patch(dim, toDraft(saved));
      setNotice("Sparat.");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  if (!drafts) {
    return <div className="text-textSecondary text-sm">Hämtar…</div>;
  }

  const field =
    "border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand";
  const labelCls = "text-xs uppercase tracking-wide text-textSecondary";

  return (
    <div className="flex flex-col gap-md max-w-3xl">
      <h1 className="font-display text-3xl tracking-tight">Wellness Plan</h1>
      <p className="text-sm text-textSecondary max-w-2xl">
        Tre dimensioner som visas i appens Wellness-sektion: aktuellt fokus +
        ett nästa litet steg. Bara publicerade dimensioner visas för patienten.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="bg-success/10 border border-success/30 text-success rounded-md p-sm text-sm">
          {notice}
        </div>
      ) : null}

      {WELLNESS_DIMENSIONS.map((dim) => {
        const draft = drafts[dim]!;
        const isNew = draft.status === "new";
        return (
          <div
            key={dim}
            className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-lg">{DIMENSION_LABELS[dim]}</h2>
              <span className="text-xs text-textSecondary">
                {isNew ? "ej skapad" : `status: ${draft.status}`}
              </span>
            </div>

            <label className="flex flex-col gap-xs w-24">
              <span className={labelCls}>Emoji</span>
              <input
                value={draft.emoji}
                onChange={(e) => patch(dim, { emoji: e.target.value })}
                className={field}
              />
            </label>

            <div className="grid grid-cols-2 gap-sm">
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Titel (sv)</span>
                <input
                  value={draft.titleSv}
                  onChange={(e) => patch(dim, { titleSv: e.target.value })}
                  className={field}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Title (en)</span>
                <input
                  value={draft.titleEn}
                  onChange={(e) => patch(dim, { titleEn: e.target.value })}
                  className={field}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Fokus (sv)</span>
                <textarea
                  value={draft.focusSv}
                  onChange={(e) => patch(dim, { focusSv: e.target.value })}
                  className={`${field} min-h-[56px]`}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Focus (en)</span>
                <textarea
                  value={draft.focusEn}
                  onChange={(e) => patch(dim, { focusEn: e.target.value })}
                  className={`${field} min-h-[56px]`}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Nästa steg (sv)</span>
                <input
                  value={draft.nextSv}
                  onChange={(e) => patch(dim, { nextSv: e.target.value })}
                  className={field}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Next step (en)</span>
                <input
                  value={draft.nextEn}
                  onChange={(e) => patch(dim, { nextEn: e.target.value })}
                  className={field}
                />
              </label>
            </div>

            {/* TES-220/221/222 — progression + CTA into deeper pillar content.
                The plan content stays stable; the week is what moves. */}
            <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-sm">
              <span className={labelCls}>Progression + CTA (valfritt)</span>
              <div className="grid grid-cols-2 gap-sm">
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>Vecka (nu)</span>
                  <input
                    type="number"
                    min={1}
                    value={draft.weekCurrent}
                    onChange={(e) =>
                      patch(dim, { weekCurrent: e.target.value })
                    }
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>Vecka (av)</span>
                  <input
                    type="number"
                    min={1}
                    value={draft.weekTotal}
                    onChange={(e) => patch(dim, { weekTotal: e.target.value })}
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>CTA-text (sv)</span>
                  <input
                    value={draft.ctaLabelSv}
                    onChange={(e) => patch(dim, { ctaLabelSv: e.target.value })}
                    placeholder="Se träning"
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>CTA label (en)</span>
                  <input
                    value={draft.ctaLabelEn}
                    onChange={(e) => patch(dim, { ctaLabelEn: e.target.value })}
                    placeholder="View training"
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs col-span-2">
                  <span className={labelCls}>
                    CTA-mål (route eller carebyella://…)
                  </span>
                  <input
                    value={draft.ctaRoute}
                    onChange={(e) => patch(dim, { ctaRoute: e.target.value })}
                    placeholder="carebyella://support/training/plans"
                    className={field}
                  />
                </label>
              </div>
            </div>

            <div className="flex items-center gap-sm">
              <button
                type="button"
                onClick={() => {
                  if (ctaPartial(draft)) {
                    setError(
                      "Fyll i CTA-text (sv + en) och CTA-mål tillsammans, eller lämna alla tomma.",
                    );
                    return;
                  }
                  void run(dim, () =>
                    isNew
                      ? createWellnessFocus(payload(dim, draft))
                      : updateWellnessFocus(dim, payload(dim, draft)),
                  );
                }}
                disabled={busy === dim}
                className="px-md py-sm rounded-md bg-brand text-white text-sm hover:bg-brand/90 disabled:opacity-50"
              >
                {busy === dim ? "Sparar…" : isNew ? "Skapa" : "Spara"}
              </button>
              {!isNew ? (
                <>
                  <button
                    type="button"
                    onClick={() => run(dim, () => publishWellnessFocus(dim))}
                    disabled={busy === dim || draft.status === "published"}
                    className="px-md py-sm rounded-md border border-brand text-brand text-sm hover:bg-brand/5 disabled:opacity-50"
                  >
                    Publicera
                  </button>
                  <button
                    type="button"
                    onClick={() => run(dim, () => archiveWellnessFocus(dim))}
                    disabled={busy === dim}
                    className="ml-auto px-md py-sm rounded-md border border-surfaceMuted text-textSecondary text-sm hover:text-danger hover:border-danger disabled:opacity-50"
                  >
                    Arkivera
                  </button>
                </>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
